/**
 * SO-9b — the catalogue: what the firm needs from this party, then every
 * engagement as a tile with its count. Pure: everything comes in as props.
 *
 * Props
 *   data        the office payload (GET /site-office-links/{token} or the firm's preview route)
 *   firmShort   the firm's short name ("Keystone Creative")
 *   subs        the party's submittals payload ({expected, handed_in, waiting_on_you}) or null
 *   readToken   the token reads go through (SO-9a R1); null when the party has no link yet
 *   sub         (path) => path, the SO-9a link helper that adds ?preview in the firm's preview
 *   locked      true in the firm's preview (party actions disabled)
 *   partyName   for the disabled title
 */
import React from 'react';

const n = (v) => (Array.isArray(v) ? v.length : Number(v) || 0);

export function needsRows({ data, subs, readToken, sub }) {
  const rows = [];
  const handed = (subs && subs.handed_in) || [];
  handed.filter((r) => r.can_confirm).forEach((r) => rows.push({
    key: `confirm-${r.id}`, tag: 'Confirm', tone: 'wait',
    text: `${r.kind_label || 'Submittal'} · ${r.title}`, sub: 'can you build it as drawn?',
    when: r.submitted_at, href: readToken ? sub(`/site-office/${readToken}/submittals`) : null, label: 'Open',
  }));
  handed.filter((r) => r.can_resubmit).forEach((r) => rows.push({
    key: `revise-${r.id}`, tag: 'Revise', tone: 'late',
    text: `${r.kind_label || 'Submittal'} · ${r.title}`, sub: `send rev ${(r.revision_number || 1) + 1}`,
    when: r.review_date, href: readToken ? sub(`/site-office/${readToken}/submittals`) : null, label: 'Open',
  }));
  (data.requests || []).filter((q) => !q.handshake && !q.declined).forEach((q) => rows.push({
    key: `price-${q.id}`, tag: 'Price', tone: 'wait',
    text: q.title || q.name || 'Price request', sub: q.due_date ? `due ${q.due_date}` : '',
    when: null, href: '#office-price', label: 'Open',
  }));
  (data.owed || []).filter((o) => o.state !== 'ok').forEach((o) => rows.push({
    key: `owed-${o.id}`, tag: 'Paperwork', tone: o.state === 'overdue' || o.state === 'missing' ? 'late' : 'wait',
    text: o.label, sub: o.due_at ? `due ${o.due_at}` : (o.state === 'wait' ? 'before first payment' : ''),
    when: null, href: '#office-paperwork', label: o.sign ? 'Read and sign' : 'Upload',
  }));
  (data.tasks || []).filter((t) => !t.done).forEach((t) => rows.push({
    key: `task-${t.id}`, tag: 'Task', tone: 'wait', text: t.title, sub: '', when: t.due_date || null,
    href: '#office-tasks', label: 'Open',
  }));
  if (n(data.questions_unseen) > 0) rows.push({
    key: 'answers', tag: 'Answered', tone: 'ok',
    text: `${n(data.questions_unseen)} answer${n(data.questions_unseen) === 1 ? '' : 's'} to your questions`,
    sub: '', when: null, href: readToken ? sub(`/site-office/${readToken}/questions`) : null, label: 'Read',
  });
  return rows;
}

const Tile = ({ label, count, note, empty, href, more, attn }) => (
  <a className="kso-tile" data-attn={attn ? '1' : undefined} href={href || undefined}
    data-testid={`office-tile-${label.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
    <span className="h">{label}</span>
    <span className="c">{count === null || count === undefined ? '' : count}</span>
    <span className={`s${empty ? ' empty' : ''}`}>{note}</span>
    <span className="l">{more} ›</span>
  </a>
);

export default function OfficeFront({ data, firmShort, subs, readToken, sub, locked, partyName }) {
  const rows = needsRows({ data, subs, readToken, sub });
  const handed = (subs && subs.handed_in) || [];
  const forYou = handed.filter((r) => r.can_confirm).length;
  const sentBack = handed.filter((r) => r.can_resubmit).length;
  const punchOpen = (data.punch || []).filter((i) => i.status !== 'done' && i.status !== 'confirmed').length;
  const tasksOpen = (data.tasks || []).filter((t) => !t.done).length;
  const owedOpen = (data.owed || []).filter((o) => o.state !== 'ok').length;
  const priceOpen = (data.requests || []).filter((q) => !q.handshake && !q.declined).length;
  const specCats = n(data.specs);
  const photosN = n((data.photos || [])[0]?.photos);
  const subsN = n(data.subs);
  const unread = n(data.messages_unread);
  const party = readToken ? (p) => sub(`/site-office/${readToken}/${p}`) : () => null;
  const notLinked = 'Available once their link exists';
  return (
    <>
      <section className="kso-card w12" data-testid="office-needs">
        <div className="kso-card__hd">What {firmShort} needs from you
          <span className="n" data-testid="office-needs-count">{rows.length ? `${rows.length} open` : 'nothing right now'}</span>
        </div>
        <div className="kso-card__bd">
          {rows.length === 0 ? <span className="k">Nothing is waiting on you.</span> : rows.map((r) => (
            <div className="kso-row" key={r.key} data-testid={`office-need-${r.key}`}>
              <span className={`kso-tag ${r.tone}`}>{r.tag}</span>
              <span className="t"><b>{r.text}</b>{r.sub ? ` · ${r.sub}` : ''}</span>
              {r.when ? <span className="m">{String(r.when).slice(0, 10)}</span> : null}
              {r.href ? (
                <a className="kso-btn brand" href={r.href} data-testid={`office-need-open-${r.key}`}>{r.label}</a>
              ) : (
                <span className="kso-btn" aria-disabled="true" title={notLinked}>{r.label}</span>
              )}
            </div>
          ))}
        </div>
      </section>
      <div className="kso-grp w12">Work</div>
      <div className="kso-cat w12" data-testid="office-catalogue-work">
        <Tile label="Tasks" count={tasksOpen} empty={!tasksOpen} note={tasksOpen ? `${tasksOpen} handed to you` : 'none handed to you'} href="#office-tasks" more="Open" attn={tasksOpen > 0} />
        <Tile label="Punch list" count={punchOpen} empty={!punchOpen} note={punchOpen ? `${punchOpen} to do` : 'nothing open'} href={party('punch')} more="Open" attn={punchOpen > 0} />
        <Tile label="Spec sheets for your work" count={specCats} empty={!specCats} note={specCats ? `${specCats} categories` : 'none yet'} href={party('specs')} more="Open" />
        <Tile label="Price requests" count={priceOpen} empty={!priceOpen} note={priceOpen ? `${priceOpen} open` : 'none open'} href="#office-price" more="Open" attn={priceOpen > 0} />
        <Tile label="Questions" count={n(data.questions_unseen) || null} empty={!n(data.questions_unseen)} note={n(data.questions_unseen) ? `${n(data.questions_unseen)} answered` : 'ask anything'} href={party('questions')} more="Ask one" attn={n(data.questions_unseen) > 0} />
        <Tile label="Submittals" count={forYou + sentBack || null} empty={!(forYou + sentBack)} note={forYou ? `${forYou} for your confirmation` : sentBack ? `${sentBack} sent back` : 'hand in your paper'} href={party('submittals')} more="Open" attn={forYou + sentBack > 0} />
      </div>
      <div className="kso-grp w12">Site</div>
      <div className="kso-cat w12" data-testid="office-catalogue-site">
        <Tile label="Photos" count={photosN || null} empty={!photosN} note={(data.photos || [])[0] ? `Visit · ${String((data.photos || [])[0].date || '').slice(0, 10)}` : 'no visits yet'} href={party('photos')} more="All photos" />
        <Tile label="Plans & drawings" count={n(data.plans) || null} empty={!n(data.plans)} note={n(data.plans) ? `${n(data.plans)} shared` : 'none shared yet'} href="#office-plans" more="Open" />
        <Tile label="Site information" count={null} note="address, access, hours" href="#office-paperwork" more="Open" />
      </div>
      <div className="kso-grp w12">You</div>
      <div className="kso-cat w12" data-testid="office-catalogue-you">
        <Tile label="Paperwork" count={owedOpen || null} empty={!owedOpen} note={owedOpen ? `${owedOpen} still needed` : 'all on file'} href="#office-paperwork" more="Open" attn={owedOpen > 0} />
        <Tile label="Messages" count={unread || null} empty={!unread} note={unread ? `${unread} new` : 'start the thread'} href="#office-messages" more="Open" attn={unread > 0} />
        <Tile label="Subs you brought" count={subsN || null} empty={!subsN} note={subsN ? `${subsN} on this job` : 'invite one'} href="#office-subs" more="Open" />
      </div>
      {locked ? <span className="kso-foot w12">Previewing as {partyName} — only they can press the party's buttons.</span> : null}
    </>
  );
}
